/**
 * Masterpass / VPOS / 3DS hata kod ve metinlerini müşteri diline çevirir.
 * hzc-order-service/services/payment/masterpass-errors.js ile aynı kurallar.
 */

export const MASTERPASS_ERROR_GENERIC = 'Ödemeniz tamamlanmadı. Lütfen tekrar deneyin.';

const BY_CODE: Record<string, string> = {
  '51': 'Kartta yeterli bakiye yok.',
  '05': 'Bankanız işlemi reddetti.',
  '12': 'İşlem bankanız tarafından kabul edilmedi.',
  '14': 'Kart bilgileri hatalı. Lütfen kontrol edin.',
  '15': 'Kartınızla ödeme şu an alınamıyor.',
  '30': 'Kart bilgileri hatalı. Lütfen kontrol edin.',
  '41': 'Kart kullanıma kapalı.',
  '43': 'Kart kullanıma kapalı.',
  '54': 'Kartın son kullanma tarihi hatalı veya geçmiş.',
  '55': 'Şifre hatalı.',
  '57': 'Kartınız bu işleme açık değil.',
  '58': 'Kartınız bu işleme açık değil.',
  '61': 'Kart limiti yetersiz.',
  '62': 'Kart e-ticaret işlemlerine kapalı.',
  '65': 'Kart limiti yetersiz.',
  '75': 'Şifre deneme hakkı doldu. Biraz sonra tekrar deneyin.',
  '82': 'Güvenlik kodu (CVV) hatalı.',
  '91': 'Banka şu an yanıt vermiyor. Lütfen tekrar deneyin.',
  '96': 'Banka şu an yanıt vermiyor. Lütfen tekrar deneyin.',
  N7: 'Güvenlik kodu (CVV) hatalı.',
  '1071': 'Doğrulama kodu hatalı veya süresi doldu.',
  '1072': 'Doğrulama kodu hatalı veya süresi doldu.',
  '1415': 'Doğrulama kodu hatalı veya süresi doldu.',
  'md:0': '3D Secure doğrulaması başarısız oldu.',
  'md:2': 'Kart 3D Secure kaydı bulunamadı. Başka kart deneyin.',
  'md:4': 'Kart 3D Secure kaydı bulunamadı. Başka kart deneyin.',
  'md:5': '3D Secure doğrulaması tamamlanamadı.',
  'md:6': '3D Secure doğrulaması tamamlanamadı.',
  'md:7': '3D Secure sırasında bir hata oluştu. Lütfen tekrar deneyin.',
  'md:8': 'Kart 3D Secure kaydı bulunamadı. Başka kart deneyin.',
};

const PHRASES: { re: RegExp; msg: string }[] = [
  {
    re: /yetersiz bakiye|insufficient|not sufficient|not enough fund|insufficient fund/,
    msg: 'Kartta yeterli bakiye yok.',
  },
  {
    // Masterpass: "Süresi dolmadan otp gönderme servisini çağıramazsınız."
    re: /otp\s*gonderme|suresi dolmadan|cagiramazsiniz|resend\s*otp/,
    msg: "Doğrulama SMS'i henüz geçerli. Lütfen gelen kodu girin; yeni kod için bir süre bekleyin.",
  },
  {
    re: /tek seferlik|sifre gecersiz|şifre geçersiz|otp.*(invalid|expired|hatal|gecersiz)|one[-\s]?time|dogrulama kodu hatal|kod hatali|kod hatalı/,
    msg: 'Doğrulama kodu hatalı veya süresi doldu.',
  },
  { re: /cvv|cvc2|\bcvc\b|guvenlik kodu|güvenlik kodu/, msg: 'Güvenlik kodu (CVV) hatalı.' },
  {
    re: /son kullanma|expired card|expiry|expiration/,
    msg: 'Kartın son kullanma tarihi hatalı veya geçmiş.',
  },
  { re: /pin hatal|incorrect pin|yanlis sifre|yanlış şifre/, msg: 'Şifre hatalı.' },
  {
    re: /yetersiz limit|limit yetersiz|exceeds (amount|limit)|limit asimi|limit aşımı/,
    msg: 'Kart limiti yetersiz.',
  },
  {
    re: /e-ticaret|internet alisveris|internet alışveriş|restricted card|islemey kapali|işleme kapalı/,
    msg: 'Kart e-ticaret işlemlerine kapalı.',
  },
  { re: /kayip|kayıp|calinti|çalıntı|stolen|lost card/, msg: 'Kart kullanıma kapalı.' },
  {
    re: /do not honor|islem reddedildi|işlem reddedildi|declined/,
    msg: 'Bankanız işlemi reddetti.',
  },
  {
    re: /zaman asimi|zaman aşımı|timeout/,
    msg: 'İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.',
  },
  { re: /3d secure|3d dogrulama|3d doğrulama/, msg: '3D Secure doğrulaması tamamlanamadı.' },
];

function fold(value: unknown): string {
  return String(value || '')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/ş/g, 's')
    .replace(/Ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/Ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/Ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/Ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/Ç/g, 'c')
    .replace(/\s+/g, ' ')
    .trim();
}

function looksTechnical(text: string): boolean {
  return /exception|vpos|http|stack|null|undefined|authorization|forbidden|correlation|statuscode|responsecode/i.test(
    text,
  );
}

function looksCustomerFacing(text: string): boolean {
  const s = String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (s.length < 8 || s.length > 140) return false;
  if (looksTechnical(s)) return false;
  if (/[çğıöşüÇĞİÖŞÜ]/.test(s)) return true;
  return /^(kart|sifre|şifre|kod|bakiye|limit|islem|işlem|odeme|ödeme|banka)/i.test(s);
}

export function humanizeMasterpassError(
  input: {
    codes?: Array<string | number | null | undefined>;
    messages?: Array<string | null | undefined>;
  } = {},
): string {
  const codes = (input.codes || [])
    .map((c) => (c == null || c === '' ? '' : String(c).trim()))
    .filter(Boolean);
  const messages = (input.messages || [])
    .map((m) => (m == null ? '' : String(m).trim()))
    .filter(Boolean);

  for (const code of codes) {
    if (BY_CODE[code] || BY_CODE[fold(code)] || BY_CODE[code.toUpperCase()]) {
      return BY_CODE[code] || BY_CODE[fold(code)] || BY_CODE[code.toUpperCase()];
    }
  }

  const hay = fold([...messages, ...codes].join(' '));
  for (const phrase of PHRASES) {
    if (phrase.re.test(hay)) return phrase.msg;
  }

  for (const message of messages) {
    if (looksCustomerFacing(message)) {
      return message.replace(/\s+/g, ' ').trim().slice(0, 140);
    }
  }

  return MASTERPASS_ERROR_GENERIC;
}
